import * as React from "react";
import styled from "styled-components";
import BodySmall from "../typography/BodySmall";
import { HorizontalSpacer, VerticalSpacer } from "../Spacers";
import { theme } from "../../../theme";
import Small from "../typography/Small";

const TextFieldStyle = styled.input<{ error?: boolean; small?: boolean }>`
  width: ${(props) => (props.small ? "100%" : "100%")};
  border: none;
  font-family: Inter;
  color: ${theme.colors.text};
  background-color: transparent;
  font-size: 14px;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const TextFieldContainer = styled.div<{
  error?: boolean;
  small?: boolean;
  fullWidth?: boolean;
  width?: string;
}>`
  display: flex;
  align-items: center;
  background-color: transparent;
  height: ${(props) => (props.small ? "auto" : "36px")};
  box-sizing: border-box;
  padding: 0 4px;

  width: ${(props) =>
    props.fullWidth
      ? "100%"
      : props.width
        ? props.width
        : props.small
          ? "150px"
          : "300px"};

  border-bottom: ${(props) =>
    props.error
      ? `1.25px solid ${theme.colors.errorDark}`
      : props.small
        ? undefined
        : `1.25px solid ${theme.colors.grey40}`};

  &:focus {
    border: ${(props) =>
      props.small ? undefined : `2px solid ${theme.colors.textSecondary}`};
    color: ${theme.colors.textSecondary};
  }
`;

interface Props {
  label?: string;
  value?: string | number;
  placeholder?: string;
  errorMessage?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  defaultValue?: string;
  small?: boolean;
  type?:
    | "text"
    | "decimal"
    | "integer"
    | "date"
    | "number"
    | "integerOnly"
    | "password"
    | "email";
  minValue?: number;
  maxValue?: number;
  icon?: React.ReactNode;
  optional?: boolean;
  fullWidth?: boolean;
  width?: string;
  name?: string;
}

const HTML_PATTERN = /[<>]/;

const TextField: React.FC<Props> = ({
  label,
  value,
  placeholder,
  errorMessage,
  onChange,
  disabled,
  defaultValue,
  small,
  type = "text",
  minValue,
  maxValue,
  icon,
  optional,
  fullWidth,
  width,
  name,
}) => {
  const [htmlWarning, setHtmlWarning] = React.useState(false);

  const handleChange = (val: string) => {
    setHtmlWarning(HTML_PATTERN.test(val));
    onChange && onChange(val);
  };

  const hasError = !!errorMessage || htmlWarning;

  return (
    <div style={{ width: fullWidth ? "100%" : "auto" }}>
      {label && (
        <>
          <div>
            <Small
              style={{ display: "inline" }}
              color={
                disabled
                  ? theme.colors.textSecondary
                  : theme.colors.textSecondary
              }
            >
              {label}
            </Small>
            {optional && (
              <Small
                color={theme.colors.textSecondary}
                style={{ marginLeft: 4, display: "inline" }}
                fontweight="400"
              >
                (Valfritt)
              </Small>
            )}
          </div>
          <HorizontalSpacer spacing={0.25} />
        </>
      )}
      {disabled ? (
        <BodySmall>{value ?? defaultValue}</BodySmall>
      ) : (
        <TextFieldContainer
          error={hasError}
          small={small}
          fullWidth={fullWidth}
          width={width}
        >
          {icon}
          {icon && <VerticalSpacer spacing={0.5} />}
          <TextFieldStyle
            defaultValue={defaultValue}
            placeholder={placeholder}
            onChange={(e) => handleChange(e.target.value)}
            value={value}
            error={hasError}
            name={name}
            small={small}
            type={type}
            title={
              type === "date"
                ? "Datum format: ÅÅÅÅ-MM-DD"
                : type === "decimal"
                  ? "Felaktigt format, använd punkt som avskiljare: 10.40"
                  : type === "integer"
                    ? "Felaktigt format, använd heltal: 10"
                    : undefined
            }
            min={minValue}
            max={maxValue}
          />
        </TextFieldContainer>
      )}
      {errorMessage && (
        <BodySmall color={theme.colors.error}>{errorMessage}</BodySmall>
      )}
      {htmlWarning && (
        <BodySmall color={theme.colors.error}>
          Vänligen ange inte HTML här.
        </BodySmall>
      )}
    </div>
  );
};

export default TextField;
